import { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  hydrateSession,
  hydratePartnerData,
  isPartnerDataCurrent,
  PartnerNotFoundError,
} from '../middleware/hydrateSession';
import { useLogout } from './useLogout';

/**
 * Ensures the app's own session markers (sessionStorage `user`/
 * `usertoken`, localStorage `email`) AND the partner/role identifiers
 * (`reg_partner_id`/`reg_shop_id`) are hydrated from the authenticated
 * Auth0 user before protected routes are allowed to render.
 *
 * sessionStorage is tab-scoped, while the Auth0 SDK's token cache lives
 * in localStorage (shared across tabs). That means a fresh tab can
 * inherit `isAuthenticated: true` from a still-valid cached token
 * without ever visiting the `/authenticate` callback that normally
 * hydrates sessionStorage — leaving routed components (e.g.
 * `ProfileDataProvider`, which reads `sessionStorage.user` directly) to
 * issue requests with empty/stale identity. This hook hydrates that
 * missing session on demand, so route gating can wait for it.
 *
 * The partner/role lookup (`reg_partner_id`/`reg_shop_id`) used to run
 * as a fire-and-forget effect elsewhere (HeaderBanner), independent of
 * this readiness gate — so a fresh login (or a fresh tab) could mount
 * protected routes like Dashboard/ProfileDataProvider before that async
 * lookup resolved, leaving them reading empty/stale IDs. It's awaited
 * here instead, so `ready` doesn't flip true until it's resolved (or a
 * mismatched role has ended the session).
 *
 * Returns `ready: true` once there's nothing to hydrate (not
 * authenticated), or both the app session and any required partner/role
 * data are confirmed present. If the partner/role lookup fails with a
 * transient error (network/5xx, as opposed to a definitive "no such
 * partner" 404) the Auth0 session is left intact and `error` is set
 * instead of logging the user out — callers can surface a retry UI and
 * call `retry()` rather than losing a valid session to an API blip.
 */
export const useHydratedSession = () => {
  const { isLoading, isAuthenticated, user, getAccessTokenSilently } =
    useAuth0();
  const doLogout = useLogout();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  const retry = useCallback(() => {
    setError(null);
    setRetryToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      // A prior logout (from this or another useHydratedSession
      // instance) may have set this before the redirect landed here —
      // clear it now that we've confirmed we're in the resulting
      // unauthenticated state, so it doesn't block a later login.
      sessionStorage.removeItem('authLoggingOut');
      setReady(true);
      return;
    }

    if (sessionStorage.getItem('authLoggingOut')) {
      // A logout triggered by this or another hook instance is already
      // in flight (the Auth0 redirect hasn't navigated away yet). Don't
      // re-derive readiness from storage that logout just cleared —
      // wait for isAuthenticated to actually flip false instead of
      // racing ahead with a stale "nothing left to hydrate" reading.
      return;
    }

    const role = localStorage.getItem('role');

    // Whether any already-stored reg_partner_id/reg_shop_id can be
    // trusted for *this* signed-in user requires knowing their email —
    // a previous account (Auth0 session expiry, a logout path that
    // intentionally leaves reg_* alone for in-progress registration
    // flows, another tab, etc.) can leave those identifiers behind
    // without ever clearing them. So unlike the app-session fast path
    // below, this can't be decided until `user` has resolved.
    if (!role) {
      if (sessionStorage.getItem('user')) {
        // An app session exists but no role was ever selected/persisted
        // for it (e.g. cleared in another tab, or a partial hydration).
        // Dashboard redirects to /user-login whenever role is missing,
        // and Login redirects straight back to /dashboard whenever
        // sessionStorage.user is set — leaving this as "ready" would
        // bounce the user between the two indefinitely. Clear the
        // stale app session instead so Login can render normally and
        // the user can go through role selection again.
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('usertoken');
      }
      setReady(true);
      return;
    } else if (
      user?.email &&
      sessionStorage.getItem('user') &&
      isPartnerDataCurrent(user.email, role)
    ) {
      setReady(true);
      return;
    }

    if (!user) {
      // Auth0 reports authenticated but hasn't resolved the user object
      // yet; wait for the next effect run instead of hydrating with
      // incomplete data.
      return;
    }

    let cancelled = false;

    (async () => {
      if (!sessionStorage.getItem('user')) {
        let accessToken: string;
        try {
          accessToken = await getAccessTokenSilently();
        } catch (hydrationError) {
          if (cancelled) {
            // A newer attempt (retry, account switch, logout) has
            // already superseded this one — don't act on a stale
            // failure (e.g. don't log out whichever session is now
            // current).
            return;
          }
          console.error(
            'Failed to hydrate app session from Auth0:',
            hydrationError
          );
          // Auth0 claims we're authenticated but a valid session/token
          // couldn't be established. Don't leave protected routes gated
          // open with no identity — end the broken Auth0 session instead.
          doLogout();
          return;
        }

        if (cancelled) {
          // Superseded while the token request was in flight — applying
          // this now would repopulate sessionStorage.user for whichever
          // account/attempt this request started under, potentially
          // clobbering a newer one.
          return;
        }
        hydrateSession(user, accessToken);
      }

      if (cancelled) {
        return;
      }

      if (role && user.email && !isPartnerDataCurrent(user.email, role)) {
        try {
          const partnerAccessToken = await getAccessTokenSilently();
          await hydratePartnerData(
            user.email,
            role,
            partnerAccessToken,
            () => cancelled
          );
        } catch (partnerError) {
          if (cancelled) {
            return;
          }

          if (partnerError instanceof PartnerNotFoundError) {
            console.error(
              'Selected role does not match this account:',
              partnerError
            );
            // A genuine role mismatch. As with any other hydration
            // failure, don't leave protected routes gated open — end
            // the session and surface the mismatch on the login page
            // once the logout redirect completes.
            sessionStorage.setItem('authNotice', 'You do not have permission');
            doLogout();
            return;
          }

          console.error(
            'Transient error resolving partner/role data:',
            partnerError
          );
          // Network error, 5xx, timeout, etc. — not evidence of a role
          // mismatch. Ending a valid Auth0 session over a temporary API
          // outage (and preventing any retry) would be worse than
          // leaving the user on a retry screen, so surface it instead.
          setError('partner-lookup-failed');
          return;
        }
      }

      if (!cancelled) {
        setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isLoading,
    isAuthenticated,
    user,
    getAccessTokenSilently,
    doLogout,
    retryToken,
  ]);

  return { isLoading, isAuthenticated, ready, error, retry };
};
